/**
 * The `fixedAsset` namespace. Its shape is the contract for every locale.
 * Interpolation: `{{name}}`, `{{code}}`, `{{count}}`, `{{date}}`, `{{from}}`, `{{to}}`.
 */
export const enUs = {
  nav: {
    myAssets: 'My assets',
    assets: 'Assets',
    locations: 'Storage locations',
    inventories: 'Inventory counts',
    import: 'Import',
  },
  common: {
    none: '—',
    unknown: 'Unknown',
    all: 'All',
    save: 'Save',
    saved: 'Saved.',
    reload: 'Reload',
    edit: 'Edit',
    add: 'Add',
    active: 'In use',
    inactive: 'Not in use',
    deactivate: 'Stop using',
    activate: 'Use again',
  },
  field: {
    code: 'Asset ID',
    name: 'Name',
    type: 'Asset type',
    accountTitle: 'Account title',
    assetClass: 'Asset class',
    tangibility: 'Tangibility',
    site: 'Site',
    location: 'Storage location',
    user: 'User',
    manager: 'Asset manager',
    status: 'Status',
    acquiredOn: 'Acquired on',
    currency: 'Currency',
    amount: 'Acquisition cost',
    amountJpy: 'Amount in JPY',
    usefulLifeYears: 'Useful life (years)',
    maker: 'Maker',
    model: 'Model',
    serialNumber: 'Serial number',
    previousCode: 'Previous asset number',
    notes: 'Notes',
    disposedOn: 'Disposed on',
    disposalMethod: 'Disposal method',
    disposalNote: 'Disposal note',
  },
  myAssets: {
    title: 'My assets',
    empty: 'No assets are assigned to you.',
    inventoryBanner:
      'Inventory count "{{title}}" is in progress. Confirm the assets you have at hand.',
    confirm: 'Confirm',
    confirmed: 'Confirmed',
  },
  assets: {
    title: 'Assets',
    register: 'Register asset',
    export: 'Export CSV',
    search: 'Search',
    searchPlaceholder: 'Asset ID, name, maker, model, serial number',
    allSites: 'All sites',
    allTypes: 'All asset types',
    openStatuses: 'All but disposed',
    jpyMissing: 'JPY amount missing',
    userDisabled: 'User disabled',
    empty: 'No assets match.',
    printLabels: 'Print labels ({{selected}})',
    clearSelection: 'Clear selection',
    selectPage: 'Select all on this page',
    selectionLimit: 'Up to {{max}} assets can be selected for labels.',
  },
  assetForm: {
    registerTitle: 'Register asset',
    editTitle: 'Edit asset',
    register: 'Register',
    registerAnother: 'Register another like this',
    registered: 'Registered {{code}}.',
    jpyHint: 'Required at sites in Japan when the currency is not JPY.',
    siteNotConfigured: "Not set up yet (an administrator sets the site's country and currency).",
    noUser: 'No user',
    chooseUser: 'Choose the user',
  },
  asset: {
    details: 'Details',
    history: 'History',
    actions: 'Actions',
    changeUser: 'Change user',
    changeManager: 'Change asset manager',
    changeLocation: 'Move',
    changeSite: 'Move to another site',
    changeStatus: 'Change status',
    dispose: 'Dispose',
    revertDisposal: 'Revert disposal',
    confirmRevert:
      'Take back the disposal of {{code}}? It returns to its status before the disposal.',
    conflict: 'This asset was changed elsewhere. Reload to see the current state.',
    jpyMissing: 'The amount in JPY is missing (required at sites in Japan).',
    userDisabled: "The user's account is disabled in mai ID. Assign the asset to someone else.",
    noUser: 'No user',
    noLocation: 'No storage location',
    hidden: 'Not shown',
    disposalNoteHint: 'Required for "Other".',
    attachments: 'Attachments',
    printLabel: 'Print label',
    confirmIn: 'Confirm in "{{title}}"',
    confirmedIn: 'Confirmed in "{{title}}"',
  },
  history: {
    empty: 'No history yet.',
    periods: 'Periods of use',
    period: '{{from}} – {{to}}',
    current: 'now',
    by: 'by {{name}}',
    system: 'System',
  },
  card: {
    title: 'Asset {{code}}',
    returnHint: 'Found this asset? Please return it to its asset manager.',
    open: 'Open the asset',
  },
  attachments: {
    photos: 'Photos',
    documents: 'Documents',
    addPhoto: 'Add photo',
    addDocument: 'Add document',
    hint: 'JPEG, PNG, WebP or PDF, up to {{maxMiB}} MB. Photos are made smaller before they are uploaded.',
    full: 'This asset has {{max}} attachments, the most it can have.',
    uploading: 'Uploading…',
    empty: 'Nothing attached yet.',
    remove: 'Remove attachment',
    removeNamed: 'Remove {{name}}',
    confirmRemove: 'Remove {{name}}? It is no longer shown, but kept as a record.',
  },
  labels: {
    title: 'Print QR labels',
    print: 'Print',
    layout: 'Label stock',
    layoutA4: 'A4 sheet, 24 labels (70 × 37 mm)',
    layoutSingle: 'Label printer (62 × 29 mm)',
    start: 'Start at label',
    startHint: 'Skips labels already used on the sheet (1 = top left).',
    summary: 'Labels: {{labels}}, pages: {{sheets}}.',
    printHint: 'In the print dialog, choose 100 % scale and no margins.',
    missing: 'Not found: {{codes}}',
    noneSelected: 'No assets are selected. Select them on the asset list.',
    toAssets: 'Go to the asset list',
  },
  inventories: {
    title: 'Inventory counts',
    start: 'Start inventory count',
    titleField: 'Title',
    scope: 'Storage locations',
    wholeSite: 'The whole site',
    startHint:
      'The count lists the assets of the site (or of the chosen storage locations) as they are now. Assets registered later are not included.',
    empty: 'No inventory counts yet.',
    startedAt: 'Started',
    closedAt: 'Closed',
    progress: '{{checked}} of {{total}} checked',
  },
  inventory: {
    close: 'Close count',
    confirmClose: 'Close "{{title}}"? Unchecked assets stay unchecked, and no asset is changed.',
    export: 'Download result (CSV)',
    expectedLocation: 'Registered location',
    expectedUser: 'Registered user',
    result: 'Result',
    foundLocation: 'Found at',
    locationDiffers: 'Location differs',
    onlyDiffers: 'Location differs only',
    selfConfirmed: 'Confirmed by the user',
    checked: 'Checked',
    note: 'Note',
    check: 'Check',
    checkTitle: 'Check {{code}}',
    found: 'Found',
    search: 'Asset ID or name',
    closedNotice:
      'This count is closed. Use the asset pages to mark missing assets as lost or to move assets to where they were found.',
  },
  import: {
    title: 'Import from CSV',
    intro:
      'Fill in the template in a spreadsheet and save it as CSV. Asset types are given by prefix, sites by name, users and asset managers by employee number. Headers and labels can be in any of the four languages.',
    template: 'Download template',
    chooseFile: 'Choose CSV file',
    encoding: 'Character encoding',
    encodings: {
      utf8: 'UTF-8',
      shiftJis: 'Shift_JIS (Excel, Japanese)',
      gb18030: 'GB18030 (Excel, Simplified Chinese)',
      big5: 'Big5 (Excel, Traditional Chinese)',
    },
    createLocations: 'Create storage locations that do not exist yet',
    preview: 'Check file',
    valid: 'No problems found. Assets to register: {{rows}}.',
    invalid:
      'Problems found: {{issues}}. Correct the file and check it again: nothing is imported while there are problems.',
    missingLocations: 'Storage locations that do not exist yet: {{names}}',
    commit: 'Import assets ({{rows}})',
    done: 'Assets registered: {{rows}}.',
    locationsCreated: 'Storage locations created: {{created}}.',
    downloadCodes: 'Download the new asset IDs (CSV)',
    row: 'Row',
    column: 'Column',
    problem: 'Problem',
    columns: {
      userEmployeeNumber: "User's employee number",
      managerEmployeeNumber: "Asset manager's employee number",
    },
    problems: {
      Required: 'Required',
      Invalid: 'Not a valid value',
      TooLong: 'Too long',
      UnknownAssetType: 'No asset type has this prefix or name',
      InactiveAssetType: 'The asset type is no longer in use',
      UnknownSite: 'No site has this name',
      SiteNotConfigured: "The site's country and currency are not set",
      UnknownLocation: 'The site has no such storage location',
      InactiveLocation: 'The storage location is no longer in use',
      UnknownEmployee: 'No user has this employee number',
      UserDisabled: "The user's account is disabled",
      NotAssetManager: 'Not an asset manager',
      StatusNotAllowed: 'Imported assets can be In use, In storage or Under repair',
      FutureDate: 'The date is in the future',
      InvalidCurrency: 'Unknown currency code',
      InvalidAmount: 'Not a valid amount for the currency',
      JpyAmountRequired: 'The amount in JPY is required at sites in Japan',
      AssetNumberExhausted: 'The asset type has no asset IDs left',
      UnknownColumn: 'Unknown column',
      DuplicateColumn: 'The column appears twice',
      MissingColumn: 'A required column is missing',
    },
  },
  locations: {
    title: 'Storage locations',
    add: 'Add storage location',
    empty: 'No storage locations at this site yet.',
  },
  assetTypes: {
    title: 'Asset types',
    description:
      'Each asset type has a prefix of 1–4 letters that starts its asset IDs (e.g. PC0000000001). The prefix can no longer change once an asset ID has been issued.',
    add: 'Add asset type',
    prefix: 'Prefix',
    prefixHint: '1–4 letters A–Z',
    prefixLocked: 'Asset IDs have been issued: the prefix is fixed.',
    defaultAssetClass: 'Default asset class',
    defaultTangibility: 'Default tangibility',
    defaultUsefulLife: 'Default useful life (years)',
    empty: 'No asset types yet.',
  },
  sites: {
    title: 'Sites',
    description:
      'Each mai ID group is a site. Assets can be registered at a site once its country and default currency are set. At sites in Japan, the acquisition cost in JPY is also required.',
    country: 'Country',
    defaultCurrency: 'Default currency',
    notConfigured: 'Not set',
    jpyRequired: 'JPY amount required',
    set: 'Set',
  },
  userPicker: {
    search: 'Name, email address or employee number',
  },
  assetManagers: {
    title: 'Asset managers',
    description:
      'Asset managers register and change assets and storage locations at every site and assign their users. Administrators can always do this.',
    add: 'Add asset manager',
    name: 'Name',
    employeeNumber: 'Employee number',
    site: 'Site',
    grantedAt: 'Added',
    managedAssets: 'Assets managed',
    remove: 'Remove',
    confirmRemove: 'Remove {{name}} as an asset manager?',
    empty: 'No asset managers have been added.',
    unknownUser: 'Unknown user',
    handOver: 'Hand over assets',
    handOverTo: 'New asset manager',
    handedOver: '{{count}} assets were handed over.',
  },
  audit: {
    title: 'Audit log',
    time: 'Time',
    actor: 'By',
    action: 'Action',
    target: 'Target',
    system: 'System',
    actions: {
      AssetTypeChanged: 'Asset type changed',
      SiteSettingsChanged: 'Site settings changed',
      StorageLocationChanged: 'Storage location changed',
      AssetManagerGranted: 'Asset manager added',
      AssetManagerRevoked: 'Asset manager removed',
      AssetDisposalReverted: 'Disposal reverted',
      AssetsImported: 'Assets imported',
      AssetCsvExported: 'Asset CSV exported',
      InventoryStarted: 'Inventory count started',
      InventoryClosed: 'Inventory count closed',
      LocalAdminGranted: 'Administrator added',
      LocalAdminRevoked: 'Administrator removed',
    },
  },
};

export type FixedAssetResources = typeof enUs;
